/**
 * FoodPulse Business Signup — two-panel funnel.
 *
 * Route: /food-pulse/business/signup
 *
 * Panel A — "I own this business" (auth required)
 *   Signed-in owner submits their own business through the existing
 *   `POST /api/foodpulse/business/signup` endpoint. Signed-out visitors
 *   see a clear sign-in CTA instead of a hard block.
 *
 * Panel B — "Suggest a business you love" (no auth required)
 *   Any visitor — logged-in or not — can flag a business missing from
 *   FoodPulse. Data goes to `POST /api/foodpulse/business/suggest`,
 *   which stores the entry for admin review and emails
 *   support@foodpulse.net.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Store, Loader2, CheckCircle2, Sparkles, LogIn } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

export default function FoodPulseBusinessSignup() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("owner");

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <main className="min-h-screen py-8" style={{ background: CREAM }} data-testid="business-signup-page">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-3xl md:text-4xl font-black text-center" style={{ color: PURPLE }}>
          Add a food business to FoodPulse
        </h1>
        <p className="text-sm text-gray-700 text-center mt-2 mb-6 max-w-2xl mx-auto">
          FoodPulse is community-powered. Whether you <b>own</b> a food business
          or just <b>know</b> one that should be listed, use the panel that fits.
        </p>

        <div className="flex gap-2 mb-6" role="tablist">
          <button onClick={() => setTab("owner")} className={`flex-1 rounded-full py-2 text-sm font-semibold border ${tab === "owner" ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={tab !== "owner" ? { borderColor: GREEN, color: GREEN } : {}} data-testid="tab-owner">
            <Store size={12} className="inline mr-1" /> I own this business
          </button>
          <button onClick={() => setTab("visitor")} className={`flex-1 rounded-full py-2 text-sm font-semibold border ${tab === "visitor" ? "bg-[#7C3AED] text-white" : ""}`} style={tab !== "visitor" ? { borderColor: PURPLE, color: PURPLE } : {}} data-testid="tab-visitor">
            <Sparkles size={12} className="inline mr-1" /> Suggest a business
          </button>
          <Link to="/food-pulse/claim" className="flex-1 rounded-full py-2 text-sm font-semibold border text-center" style={{ borderColor: GREEN, color: GREEN }} data-testid="tab-claim">
            <Building2 size={12} className="inline mr-1" /> Claim existing
          </Link>
        </div>

        {tab === "owner"
          ? <OwnerPanel user={user} nav={nav} params={params} />
          : <VisitorPanel user={user} />
        }
      </div>
    </main>
  );
}

// ─── Owner panel — auth-required ─────────────────────────────────
function OwnerPanel({ user, nav, params }) {
  const [form, setForm] = useState({
    name: params.get("name") || "",
    address: "",
    phone: "",
    website: "",
    category: params.get("category") || "",
    email: "",
    notes: "",
  });
  const [busy, setBusy]   = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState("");
  const [mine, setMine]   = useState([]);

  useEffect(() => {
    if (!user) return;
    http.get("/foodpulse/business/signup/mine").then((r) => setMine(r.data.items || [])).catch(() => {});
  }, [user, saved]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string" && v.trim() === "") continue;
        payload[k] = v;
      }
      const r = await http.post("/foodpulse/business/signup", payload);
      setSaved(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ")
                                    : (typeof d === "string" ? d : "Submit failed");
      setError(msg);
    }
    setBusy(false);
  };

  // Signed-out state — clear sign-in prompt instead of a blank block.
  if (!user) {
    return (
      <div className="rounded-2xl bg-white border p-6 text-center" style={{ borderColor: "#E9D8B4" }} data-testid="owner-signin-cta">
        <Store size={40} className="mx-auto mb-3" style={{ color: GREEN }} />
        <h2 className="text-xl font-bold" style={{ color: GREEN }}>Business owners — sign in to add your business</h2>
        <p className="text-sm text-gray-700 mt-2 max-w-md mx-auto">
          Signing in ties your business to your account so you can update photos,
          hours, deals, and inventory later. It only takes 30 seconds.
        </p>
        <div className="flex justify-center gap-2 mt-4">
          <Link to="/auth/signin?next=/food-pulse/business/signup" className="rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-1" style={{ background: GREEN, color: CREAM }} data-testid="owner-signin-btn">
            <LogIn size={14} /> Sign in
          </Link>
          <Link to="/auth/register?next=/food-pulse/business/signup" className="rounded-full px-5 py-2 text-sm font-semibold border" style={{ borderColor: GREEN, color: GREEN }} data-testid="owner-register-btn">
            Create an account
          </Link>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Not the owner? Use <b>Suggest a business</b> above — no sign-in required.
        </p>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="rounded-2xl bg-white border p-6 text-center" style={{ borderColor: "#E9D8B4" }} data-testid="owner-signup-success">
        <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: GREEN }} />
        <h2 className="text-xl font-bold" style={{ color: GREEN }}>Thanks — you're in the queue</h2>
        <p className="text-sm text-gray-600 mt-2">
          Our team will review <b>{saved.name}</b> and reach out at{" "}
          <b>{saved.owner_email || user.email}</b>. Approvals usually take under 24 hours.
        </p>
        <button onClick={() => nav("/food-pulse")} className="mt-4 rounded-full px-5 py-2 text-sm font-semibold" style={{ background: PURPLE, color: "white" }}>
          Back to FoodPulse
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl bg-white border p-5" style={{ borderColor: "#E9D8B4" }}>
        <h2 className="text-lg font-bold mb-1" style={{ color: GREEN }}>Your business details</h2>
        <p className="text-xs text-gray-600 mb-4">Signed in as <b>{user.email}</b>. You'll own the listing once approved.</p>
        <div className="grid gap-3" data-testid="owner-signup-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business name *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="biz-name-input" />
          <textarea rows="2" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="biz-address-input" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Business phone *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="biz-phone-input" />
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="biz-website-input" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="biz-cat-select">
            <option value="">Category (optional)</option>
            {["Supermarket","Grocery","Restaurant","Bakery","Farm","Butcher","Fishmonger","Caterer","Food Truck","Convenience Store","Warehouse Club","Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Business email (optional)" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else we should know?" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
          {error && <div className="text-sm text-red-700" data-testid="owner-error">{error}</div>}
          <button onClick={submit} disabled={busy || !form.name || !form.address || !form.phone} className="rounded-full py-3 font-semibold text-sm disabled:opacity-50" style={{ background: PURPLE, color: "white" }} data-testid="owner-submit-btn">
            {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Submit for review"}
          </button>
        </div>
      </div>

      {mine.length > 0 && (
        <div className="mt-6 rounded-2xl bg-white border p-4" style={{ borderColor: "#E9D8B4" }} data-testid="my-signups">
          <h3 className="text-sm font-bold mb-2" style={{ color: GREEN }}>Your submissions</h3>
          <ul className="text-sm space-y-1">
            {mine.map((m) => (
              <li key={m.id} className="flex items-center justify-between border-b py-1 last:border-0" style={{ borderColor: "#F5E6C4" }}>
                <span>{m.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: m.status === "approved" ? "#BBF7D0" : m.status === "rejected" ? "#FCA5A5" : "#FDE68A", color: GREEN }}>{m.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ─── Visitor panel — no auth required ────────────────────────────
function VisitorPanel({ user }) {
  const [form, setForm] = useState({
    suggester_name:  user?.name || "",
    suggester_email: user?.email || "",
    suggester_phone: "",
    business_name:     "",
    business_address:  "",
    business_category: "",
    business_phone:    "",
    business_email:    "",
    business_website:  "",
    city:              "",
    state:             "",
    notes:             "",
  });
  const [busy, setBusy]   = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "string" && v.trim() === "") continue;
        payload[k] = v;
      }
      const r = await http.post("/foodpulse/business/suggest", payload);
      setSaved(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ")
                                    : (typeof d === "string" ? d : "Submit failed");
      setError(msg);
    }
    setBusy(false);
  };

  if (saved) {
    return (
      <div className="rounded-2xl bg-white border p-6 text-center" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-signup-success">
        <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: PURPLE }} />
        <h2 className="text-xl font-bold" style={{ color: GREEN }}>Thanks — we've got it</h2>
        <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
          Our team will review <b>{saved.business_name}</b> and add it to the directory
          if it fits FoodPulse. If we need more info, we'll email <b>{saved.suggester_email}</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border p-5" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-suggest-form">
      <h2 className="text-lg font-bold mb-1" style={{ color: PURPLE }}>Know a food business worth adding?</h2>
      <p className="text-xs text-gray-600 mb-4">
        Anyone can suggest — you don't need an account. Fill in whatever you know;
        our editors will do the research for the rest. A copy is emailed to{" "}
        <b>support@foodpulse.net</b> and held for review.
      </p>

      <div className="grid gap-3">
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: GREEN }}>About you</div>
        <input value={form.suggester_name} onChange={(e) => setForm({ ...form, suggester_name: e.target.value })} placeholder="Your name *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-name-input" />
        <input value={form.suggester_email} onChange={(e) => setForm({ ...form, suggester_email: e.target.value })} placeholder="Your email *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-email-input" />
        <input value={form.suggester_phone} onChange={(e) => setForm({ ...form, suggester_phone: e.target.value })} placeholder="Your phone (optional)" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-phone-input" />

        <div className="text-[11px] uppercase tracking-widest font-bold mt-2" style={{ color: GREEN }}>About the business</div>
        <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business name *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-biz-name-input" />
        <textarea rows="2" value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} placeholder="Address *" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-biz-address-input" />
        <div className="grid grid-cols-2 gap-3">
          <input value={form.city}  onChange={(e) => setForm({ ...form, city:  e.target.value })} placeholder="City"  className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-city-input" />
          <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-state-input" />
        </div>
        <select value={form.business_category} onChange={(e) => setForm({ ...form, business_category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-cat-select">
          <option value="">Category (optional)</option>
          {["Supermarket","Grocery","Restaurant","Bakery","Farm","Butcher","Fishmonger","Caterer","Food Truck","Convenience Store","Warehouse Club","Other"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={form.business_phone}   onChange={(e) => setForm({ ...form, business_phone:   e.target.value })} placeholder="Business phone (optional)"   className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
        <input value={form.business_email}   onChange={(e) => setForm({ ...form, business_email:   e.target.value })} placeholder="Business email (optional)"   className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
        <input value={form.business_website} onChange={(e) => setForm({ ...form, business_website: e.target.value })} placeholder="Website (optional)"          className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} />
        <textarea rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else we should know? Menu highlights, opening hours, why you love them…" className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: "#E9D8B4" }} data-testid="visitor-notes-input" />

        {error && <div className="text-sm text-red-700" data-testid="visitor-error">{error}</div>}
        <button
          onClick={submit}
          disabled={busy || !form.suggester_name || !form.suggester_email || !form.business_name || !form.business_address}
          className="rounded-full py-3 font-semibold text-sm disabled:opacity-50"
          style={{ background: PURPLE, color: "white" }}
          data-testid="visitor-submit-btn"
        >
          {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Send suggestion"}
        </button>
      </div>
    </div>
  );
}
